import { useState, useCallback, useEffect, useImperativeHandle } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button, CreditCard, ConfirmationCodeInput, Divider } from '@/components'
import { Icons } from '@/theme/Images'
import { dummyData } from '@/data'
import useOtp from '../hooks/use-otp'
import { toast } from 'sonner'
import { ToastMessage } from '../../components/toast-message'
import { useAuth } from '../../contexts/auth.context'
import { CardViewerComponent } from '../payment-card-sdk/card-viewer'
import useGenerateClientToken from '../hooks/use-generate-client-token.jsx'
import { useSearchParams } from 'react-router-dom'
import { getOTPErrorMessage } from '@/utils/error'
import PropTypes from 'prop-types'
import { useViewCardTokenSession } from '@/hooks/use-viewcard-token-session'

const CardDetailsDialog = ({ viewCardDetailsFnRef }) => {
  const [isVerified, setIsVerified] = useState(false)
  const [code, setCode] = useState('')
  const [isCodeSent, setIsCodeSent] = useState(false)
  const [codeError, setCodeError] = useState()
  const [searchParams, setSearchParams] = useSearchParams()
  const isDeepLinkOpen = searchParams?.get?.('view-card-details') === 'true'
  const [isOpen, setIsOpen] = useState(isDeepLinkOpen)
  const { user } = useAuth()
  const cardDetails = user.getViewCardDetails()
  const { setViewCardToken, getValidViewCardToken } = useViewCardTokenSession(cardDetails?.paymentCardId)
  const [token, setToken] = useState()
  const [loading, setLoading] = useState(false)

  const { mutateAsync: sendOtp, isPending: otpPending } = useOtp()
  const { mutateAsync: generateClientToken } = useGenerateClientToken()

  const handleGenerateClientToken = useCallback(async () => {
    try {
      const response = await generateClientToken({ code, cardId: cardDetails?.paymentCardId })
      console.log('Generated Client Token Response:', response)
      setIsVerified(true)
      setToken(response.value)
      setViewCardToken(response.value, response.expirationDate)
    } catch (error) {
      console.error('Error generating client token:', error)
      setCodeError('The code is incorrect or expired. Try again.')
      setIsVerified(false)
    }
  }, [cardDetails?.paymentCardId, code, generateClientToken, setViewCardToken])

  //Handle verify button click
  const handleVerify = useCallback(async () => {
    try {
      setLoading(true)
      if (cardDetails?.paymentCardId) {
        await handleGenerateClientToken()
      } else {
        toast.custom(() => (
          <ToastMessage
            text="Failed"
            success={false}
            description={
              'A locked or unlocked card is required to proceed. Please try again later or contact customer support for assistance.'
            }
          />
        ))
      }
    } finally {
      setLoading(false)
    }
  }, [cardDetails?.paymentCardId, handleGenerateClientToken])

  useEffect(() => {
    const handleKeyDown = e => {
      if (e?.key === 'Enter' && code?.length === 6) {
        handleVerify()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [code, handleVerify])

  //Handle events on open and close
  const handleDialogOpenClose = val => {
    setIsOpen(val)
    setIsVerified(false)
    setCodeError('')
    setCode()
    setIsCodeSent(false)
  }

  const openViewCardInfoWithSession = () => {
    const token = getValidViewCardToken()
    if (token) {
      setToken(token)
      setIsVerified(true)
      setIsCodeSent(true)
      setIsOpen(true)
    } else handleDialogOpenClose(true)
  }

  //Handle verification code change
  const handleVerificationCodeChange = input => {
    setCode(input)
    setCodeError()
  }

  //Handle sending OTP code
  const handleOTPSend = async () => {
    await sendOtp()
      .then(() => setIsCodeSent(true))
      .catch(err => toast.custom(() => <ToastMessage text="Failed" description={getOTPErrorMessage(err)} />))
  }

  //Handle resend code
  const onResendCode = () => {
    handleOTPSend()
    setIsCodeSent(true)
    setCodeError('')
    setCode('')
  }

  //Clear code and error on input click
  const onClickCodeInput = () => {
    if (codeError) {
      setCode('')
      setCodeError('')
    }
  }

  // Clear deep link search param if dialog is closed
  useEffect(() => {
    if (!isOpen) {
      setSearchParams({})
    }
  }, [isOpen, setSearchParams])

  useImperativeHandle(viewCardDetailsFnRef, () => ({
    openViewCardInfoWithSession,
  }))

  return (
    <Dialog onOpenChange={handleDialogOpenClose} open={isOpen}>
      <DialogTrigger asChild>
        <p data-testid="card-detail-dialog-trigger" />
      </DialogTrigger>
      <DialogContent
        className="max-w-[92%] rounded-lg md:max-w-[480px] p-0"
        data-testid="card-dialog-content"
        onInteractOutside={event => {
          event.preventDefault()
        }}
      >
        <DialogHeader className="p-5 pb-4">
          <div className="flex flex-row justify-start items-start">
            <img src={Icons.filledCardIcon} alt="icon" width={48} />
            <div className="ml-5 flex flex-col justify-center items-start">
              <DialogTitle className="leading-7 text-neutral-900 text-lg font-semibold">Card Details</DialogTitle>
              {!isCodeSent ? (
                <DialogDescription className="text-neutral-700 text-sm font-normal text-start">
                  To view your card details, we need you to verify your identity. We&apos;ll send a confirmation text to
                  (***) ***-{user?.customer?.maskedPhoneNumber?.slice(-4)}. Please note that the code will expire 10
                  minutes after being sent.
                </DialogDescription>
              ) : isVerified ? (
                <DialogDescription className="text-neutral-700 text-sm font-normal text-start">
                  Use this card anywhere you shop or pay online.
                </DialogDescription>
              ) : (
                <DialogDescription className="text-neutral-700 text-sm font-normal text-start">
                  Please enter the temporary 6-digit code we sent to (***) ***-
                  {user.customer.maskedPhoneNumber?.slice(-4)}. Didn&apos;t receive a code?{' '}
                  <span
                    className="text-brand-500 text-sm font-semibold text-start cursor-pointer md:inline block"
                    onClick={onResendCode}
                  >
                    Resend Code
                  </span>
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>
        {isCodeSent ? (
          <>
            <Divider />
            <div className="px-5 pt-4">
              {!isVerified ? (
                <CreditCard masked={!isVerified} data={dummyData.mockCreditCardData} blurred={true} />
              ) : (
                <CardViewerComponent
                  clientToken={token}
                  paymentCardId={cardDetails?.paymentCardId}
                  onError={error => console.error('Card Error:', error)}
                  onReady={() => console.log('Card details loaded')}
                  cardHolderName={`${user?.customer?.firstName?.toUpperCase() || ''} ${user?.customer?.lastName?.toUpperCase() || ''}`}
                />
              )}
              {!isVerified && (
                <>
                  <p className="text-[#1A2022] text-sm font-medium mb-[-10px] my-2">Code</p>
                  <div className="mt-5 mb-8" onClick={onClickCodeInput}>
                    <ConfirmationCodeInput code={code} setCode={handleVerificationCodeChange} length={6} />
                  </div>
                </>
              )}
              {codeError && !isVerified ? (
                <div className="relative flex flex-row justify-start items-center px-1 pb-8 mt-[-18px]">
                  <div className="relative flex justify-center items-center mt-[-15px] md:mt-0">
                    <img src={Icons.xCircleIcon} />
                    <div className="absolute rounded-full border-2 border-error-700 opacity-10 h-9 w-9" />
                    <div className="absolute rounded-full border-2 border-error-700 opacity-40 h-[26px] w-[26px]" />
                  </div>
                  <p className="ml-6 text-sm font-semibold text-error-500">{codeError}</p>
                </div>
              ) : null}
            </div>
          </>
        ) : null}
        <Divider />
        <DialogFooter className="w-full px-5">
          {!isCodeSent ? (
            <>
              <Button
                id="viewing-card-details-cancel-button"
                aria-label="Cancel"
                className="w-full"
                variant="outlinePrimary"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                id="send-verification-code-button"
                aria-label="Send Code"
                className="w-full"
                disabled={otpPending}
                onClick={handleOTPSend}
              >
                Send Code
              </Button>
            </>
          ) : isVerified ? (
            <Button
              id="card-details-close-button"
              aria-label="Close"
              className="w-full"
              onClick={() => setIsOpen(false)}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                id="card-details-verify-cancel-button"
                aria-label="Cancel"
                variant="outlinePrimary"
                className="w-full"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                id="verify-button"
                aria-label="Verify"
                className="w-full"
                disabled={Boolean(code?.length !== 6) || loading}
                onClick={handleVerify}
              >
                Verify
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

CardDetailsDialog.propTypes = {
  viewCardDetailsFnRef: PropTypes.object,
}

export default CardDetailsDialog
